import React from 'react';
import { useChat } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';
import { CheckCircle, AlertCircle, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Usage Indicator Component
 * 
 * Shows remaining calculations with progress bar
 */
const UsageIndicator = ({
  variant = 'default',
  showUpgrade = true,
  className = '',
}) => {
  const { user } = useAuth();
  const { getRemainingCalcs, isPro, usageData } = useChat();

  const remaining = getRemainingCalcs();
  const total = 10;
  const used = total - (remaining === Infinity ? 0 : remaining);
  const percentage = isPro ? 0 : (used / total) * 100;

  // Pro users - show Pro badge
  if (isPro) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <div className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-full text-sm font-medium">
          <Sparkles className="w-4 h-4" />
          Pro - Unlimited
        </div>
      </div>
    );
  }

  // Compact variant for navbar
  if (variant === 'compact') {
    return (
      <div className={`flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm ${className}`}>
        <span className="font-medium">{remaining}/10</span>
        <span className="text-blue-500">calcs</span>
      </div>
    );
  }

  // Default variant with progress bar
  return (
    <div className={`bg-white rounded-xl p-4 border ${className}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">
          Calculations This Month
        </span>
        <span className="text-sm text-gray-500">
          {used} / {total} used
        </span>
      </div>
      
      {/* Progress bar */}
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-3">
        <div 
          className={`h-full rounded-full transition-all duration-500 ${
            percentage >= 80 ? 'bg-red-500' : percentage >= 50 ? 'bg-amber-500' : 'bg-blue-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      
      {/* Status message */}
      <div className="flex items-center justify-between">
        {remaining > 0 ? (
          <div className="flex items-center gap-2 text-sm text-green-600">
            <CheckCircle className="w-4 h-4" />
            <span>{remaining} calculations remaining</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-sm text-red-600">
            <AlertCircle className="w-4 h-4" />
            <span>Limit reached</span>
          </div>
        )}
        
        {showUpgrade && (
          <Link 
            to="/pricing"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Upgrade →
          </Link>
        )}
      </div>
      
      {/* Reset date */}
      {user && usageData.reset_date && (
        <p className="text-xs text-gray-400 mt-2">
          Resets on {new Date(usageData.reset_date).toLocaleDateString()}
        </p>
      )}
    </div>
  );
};

export default UsageIndicator;
